import React, { useEffect } from "react";
import {
  SITE_NAME,
  PRIVACY_UPDATED,
  CONTACT_URL,
  CONTACT_LABEL,
} from "../constants/siteConfig";

export default function PrivacyPolicy({ onClose }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div className="legal-backdrop" onClick={onClose}>
      <article
        className="legal-modal"
        role="dialog"
        aria-label="Privacy Policy"
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2>Privacy Policy</h2>
          <button
            className="slide-panel-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="legal-body">
          <p className="legal-updated">
            Last updated: {PRIVACY_UPDATED}
          </p>

          <p>
            {SITE_NAME} is a community map and open-data project for
            shipwrecks. This Privacy Policy explains what information we
            collect, how we use it, what information may be public, and the
            choices available to you. You can browse the public map without
            creating an account.
          </p>

          <h3>1. Information we collect</h3>

          <p>
            <strong>Account information.</strong> If you create an account, we
            store your username, email address, name, country, and a securely
            hashed version of your password. We do not store your password in
            readable form. Your profile may also contain an optional bio or
            avatar URL if you choose to provide one.
          </p>

          <p>
            <strong>Submissions and edits.</strong> If you submit or edit a
            shipwreck, we store the information you provide, which may include
            the ship name, coordinates, historical and descriptive details,
            dates, links, photos or photo URLs, photo attribution, and
            per-photo credit/license information. We also associate submissions
            and approved edits with the submitting username for attribution
            and moderation purposes.
          </p>

          <p>
            <strong>Images and image attribution.</strong> Images submitted to{" "}
            {SITE_NAME} may be accompanied by attribution, copyright, license,
            source, credit, or other ownership information. This information
            may be displayed publicly alongside the image so that the creator
            or copyright holder can be properly credited.
          </p>

          <p>
            <strong>Attribution is required for photos.</strong> Every photo
            submitted with a new shipwreck or an edit must include a credit
            identifying the photographer or the source of the image. A
            submission or edit that includes a photo without a credit cannot
            be sent for review. The credit (and the optional license) you
            enter is stored with your submission and shown publicly beside the
            photo. Photos that were published before this requirement may not
            have a credit; if you are the author of one of them and want it
            credited or removed, please contact us.
          </p>

          <p>
            <strong>Image removal requests.</strong>{" "}
            <strong>
              If you are the author, photographer, copyright holder, or
              otherwise have rights to an image displayed on {SITE_NAME} and
              would like that image removed, please contact us and request its
              removal.
            </strong>{" "}
            You do not need to have an account to request removal of an image.
          </p>

          <p>
            Please provide enough information for us to identify the image and,
            where possible, information showing that you are the author,
            copyright holder, or authorized representative. We may contact you
            for additional information when necessary to process the request.
          </p>

          <p>
            <strong>Search.</strong> The ship search box (including search by
            boat ID) runs in your browser against the list of public
            shipwrecks it has already loaded. What you type into the search
            box is not sent to our server and is not stored.
          </p>

          <p>
            <strong>Contacting us.</strong> The Contact page does not collect
            anything itself. It links to {CONTACT_LABEL}, which is hosted by
            GitHub, and to our profiles on X and Reddit. Anything you post
            on those sites, including your username there and the content of
            your message, is handled under that site's own privacy statement,
            not this one, and public issues and posts can be seen by anyone.
            Please do not post private personal information in a public issue
            or post. The About page is static and collects no information.
          </p>

          <p>
            <strong>Reports.</strong> If you are signed in and use the
            "Report" button on a shipwreck entry, we store your username, the
            entry, the reason you chose, any note you write, and when you sent
            it. Reports are visible only to site administrators, who may reply
            to you through a notification. If you delete your account, your
            reports are kept without your username (shown as "deleted").
          </p>

          <p>
            <strong>Badges.</strong> Contributor badges are calculated from
            your approved contributions and shown on your public profile and
            the contributors leaderboard. They are not stored separately.
          </p>

          <p>
            <strong>Favorites and profile activity.</strong> If you are signed
            in, we store the shipwrecks you mark as favorites and information
            needed to display your account and contribution history.
          </p>

          <p>
            <strong>Session information.</strong> When you sign in, the site
            creates a random session token associated with your account and
            stores that token in a browser cookie named <code>session</code>.
            The cookie allows you to remain signed in and is removed when you
            sign out.
          </p>

          <p>
            <strong>Security and rate-limiting information.</strong> We may
            temporarily process your IP address and the username attempted when
            limiting repeated sign-in or sign-up attempts. Failed-login
            counters are automatically cleared after their short rate-limiting
            window, and sign-up rate-limit records are kept only for their
            applicable window.
          </p>

          <p>
            <strong>Technical information.</strong> Cloudflare provides
            hosting, database, networking, security, and other infrastructure
            for the site. As part of providing those services, Cloudflare may
            process information such as IP addresses, browser/request
            information, and security signals.
          </p>

          <p>
            <strong>Location information.</strong> If you use the site's
            location feature, your browser asks for permission to provide your
            current location. The location is used in your browser to center
            the map, to list the wrecks nearest to you, or to populate a
            shipwreck submission. It is sent to our
            server only when you choose to submit the coordinates as part of a
            submission or edit.
          </p>

          <p>
            <strong>Draft data.</strong> While you are preparing a submission,
            the site may save the draft in your browser's local storage so you
            do not lose your work. This draft is stored on your device rather
            than in our database and is removed when the draft is submitted or
            discarded.
          </p>

          <h3>2. How we use information</h3>

          <ul>
            <li>
              To operate accounts, authentication, favorites, profiles,
              submissions, and edits.
            </li>
            <li>
              To review, moderate, approve, reject, publish, and attribute
              community contributions.
            </li>
            <li>
              To maintain the public shipwreck map, public profiles,
              contribution history, and change history.
            </li>
            <li>
              To provide public data downloads of approved shipwreck
              information.
            </li>
            <li>
              To prevent abuse, automated account creation, password guessing,
              and unauthorized access.
            </li>
            <li>
              To maintain, secure, troubleshoot, and improve the service.
            </li>
            <li>
              To respond to copyright, attribution, image removal, privacy, and
              other content requests.
            </li>
          </ul>

          <p>
            We do not sell your personal information. We do not use third-party
            advertising trackers or third-party analytics services for
            behavioral advertising.
          </p>

          <h3>3. What information is public</h3>

          <p>
            Approved shipwreck entries are public. Depending on what you
            submit, a published entry may include the wreck's name, location,
            historical or descriptive information, links, photos, photo
            attribution, and photo credit/license information. The username
            associated with the submission may also be displayed as a
            contributor.
          </p>

          <p>
            Approved edits may be attributed to the usernames of contributors,
            and the site may maintain a public change history showing
            contribution and review information. Please do not put private or
            sensitive personal information about yourself or anyone else into a
            shipwreck submission, edit, photo credit, description, or link.
          </p>

          <p>
            Your account password, password hash, session token, email address,
            and other private account fields are not included in public
            shipwreck data or public data exports. Favorites are associated
            with your account and are not part of the public shipwreck export.
          </p>

          <h3>4. Public data exports</h3>

          <p>
            {SITE_NAME} provides public CSV and GeoJSON downloads of approved
            shipwreck records. These exports contain public shipwreck
            information such as names, coordinates, descriptions, contributor
            attribution, links, and other wreck details.
          </p>

          <p>
            <strong>
              Photos and photo-credit metadata are intentionally excluded from
              these exports.
            </strong>{" "}
            Private account information and pending submissions are also
            excluded.
          </p>

          <h3>5. Third-party services</h3>

          <p>
            The site relies on third-party infrastructure and content
            providers. When your browser connects to those services, they may
            receive your IP address, browser information, and other information
            normally included in a web request.
          </p>

          <ul>
            <li>
              <strong>Cloudflare.</strong> Cloudflare provides hosting,
              database, networking, security, and may provide Turnstile
              anti-bot verification when Turnstile is enabled for the
              deployment.
            </li>

            <li>
              <strong>Map providers.</strong> Depending on the map style
              selected, map tiles may be loaded from OpenStreetMap-related tile
              services or Stadia Maps/Stamen/OpenMapTiles services.
            </li>

            <li>
              <strong>Google Fonts.</strong> The site loads its fonts from
              Google Fonts, so your browser may contact Google's font servers
              when loading the site.
            </li>

            <li>
              <strong>Image hosts.</strong> When a published shipwreck contains
              externally hosted photo URLs, your browser loads those images
              directly from the image host. That host may receive your IP
              address and normal request information.
            </li>

            <li>
              <strong>GitHub.</strong> The Contact and About pages link to our
              project page on GitHub. GitHub is contacted only when you follow
              those links, and issues you open there are public.
            </li>

            <li>
              <strong>Google Maps.</strong> Some shipwreck and yard pages
              provide links to Google Maps. Google is contacted only when you
              follow those links.
            </li>
          </ul>

          <p>
            These third-party services have their own privacy policies and
            terms. Their processing is governed by their respective policies
            rather than this one.
          </p>

          <h3>6. Account deletion and retention</h3>

          <p>
            You can delete your account from the account settings area when
            that option is available. Deleting an account removes the account
            record and associated private account information, including the
            password hash, email, name, favorites, and active account
            information.
          </p>

          <p>
            Deleting an account does <strong>not</strong> automatically delete
            shipwreck work that you submitted or edits that you proposed.
            Approved, pending, and rejected submissions and edits may be
            retained as part of the site's historical and moderation records.
            Where the system can identify your authorship, your username is
            replaced with <strong>"deleted"</strong> on retained contributions
            and relevant change-history records.
          </p>

          <p>
            Shipwreck data, photos, links, and approval history may therefore
            remain after account deletion. If you believe specific publicly
            displayed content should be removed, contact us using the contact
            method below and explain the request.
          </p>

          <p>
            An administrator may disable an account for abuse or rule
            violations. A disabled account cannot sign in, but disabling an
            account does not by itself remove its contributions.
          </p>

          <h3>7. Image removal and copyright requests</h3>

          <p>
            {SITE_NAME} respects the rights of photographers, artists,
            copyright holders, and other content creators.
          </p>

          <p>
            <strong>
              If you are the author or rights holder of an image appearing on{" "}
              {SITE_NAME} and do not want your image displayed, please reach out
              to us and request that the image be removed.
            </strong>
          </p>

          <p>
            Image removal requests can be submitted even if the image was
            uploaded by another {SITE_NAME} user. We may ask for the image URL,
            shipwreck record, original source, proof of authorship or
            ownership, or other information needed to evaluate the request.
          </p>

          <p>
            If an image is removed, associated attribution or metadata may also
            be removed or retained where necessary to preserve the history and
            integrity of the database.
          </p>

          <p>
            We encourage users to provide accurate photo attribution and
            licensing information when submitting images. Providing attribution
            does not prevent the copyright holder or author from contacting us
            to request removal.
          </p>

          <h3>8. Your choices and rights</h3>

          <p>
            You can sign out, change your password, update available profile
            information, manage favorites, and delete your account through the
            site. Depending on where you live, you may have additional privacy
            rights, such as rights to request access to, correction of, or
            deletion of personal information, or to object to or restrict
            certain processing.
          </p>

          <p>
            To make a privacy request, copyright request, attribution
            correction, or image removal request, contact us using the link
            below. We may need enough information to verify the request and
            protect the account or other users from unauthorized requests.
          </p>

          <h3>9. Security</h3>

          <p>
            Passwords are stored using a password-hashing process rather than
            readable passwords. Sessions use random tokens, traffic is served
            over HTTPS when the site is deployed through its normal Cloudflare
            configuration, and authentication endpoints use rate limiting. No
            internet service can guarantee absolute security, so please use a
            strong, unique password and do not reuse passwords from other
            services.
          </p>

          <h3>10. Children</h3>

          <p>
            {SITE_NAME} is not directed to children under 13, and we do not
            knowingly request personal information from children under 13. If
            you believe a child has created an account, contact us so we can
            review the situation and take appropriate action.
          </p>

          <h3>11. Changes to this policy</h3>

          <p>
            We may update this Privacy Policy as the site changes. When we
            make a change, we will update the "Last updated" date above. Your
            continued use of the site after an updated policy is posted means
            the updated policy will apply to your use of the site going
            forward.
          </p>

          <h3>12. Contact</h3>

          <p>
            Questions, privacy requests, copyright concerns, attribution
            corrections, or requests to have an image removed from {SITE_NAME}{" "}
            can be sent through{" "}
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {CONTACT_LABEL}
            </a>
            .
          </p>
        </div>

        <footer>
          <button className="panel-primary" onClick={onClose}>
            Close
          </button>
        </footer>
      </article>
    </div>
  );
}